import {
  MomoScheduleDayDto,
  MomoScheduleDayShiftDto,
  MomoScheduleDayTaskListDto,
  MomoScheduleDayTypeDto,
  MomoScheduleDto,
  MomoScheduleMonthDto,
  MomoScheduleObservationsDto,
  MomoScheduleWeekDto
} from '@app/data/dtos/mot/schedule/momo-schedule.dto';
import { ShiftTypes, ShiftTypesBackgroundColors, ShiftTypesColors } from '@app/entities/enums/momo/shift-types.enum';
import {
  MomoSchedule,
  MomoScheduleDay,
  MomoScheduleDayShift,
  MomoScheduleDayTaskList,
  MomoScheduleDayType,
  MomoScheduleMonth,
  MomoScheduleObservations,
  MomoScheduleWeek
} from '@app/entities/models/mot/schedule/momo-schedule.model';

import { MapFromFn } from '@mercadona/common/public';
import { Builder } from '@mercadona/common/utils';

export const mapMomoSchedule: MapFromFn<MomoScheduleDto, MomoSchedule> = (value) =>
  Builder.forModel(MomoSchedule)
    .months((value?.months || []).map((month) => mapMomoScheduleMonth(month)))
    .build();

export const mapMomoScheduleDay: MapFromFn<MomoScheduleDayDto, MomoScheduleDay> = (value) =>
  Builder.forModel(MomoScheduleDay)
    .date(value.date)
    .dayLabel(value.day_label)
    .dayName(value.day_name)
    .centerNumber(value.center_number)
    .dayType(mapMomoScheduleDayType(value.day_type))
    .taskList((value?.task_list || []).map((item) => mapMomoScheduleDayTaskList(item)))
    .schedule(mapMomoScheduleDayShift(value.schedule))
    .build();

export const mapMomoScheduleDayType: MapFromFn<MomoScheduleDayTypeDto | null, MomoScheduleDayType | null> = (value) => {
  if (!value) {
    return null;
  }

  return Builder.forModel(MomoScheduleDayType)
    .ids(value.ids)
    .name(value.name)
    .primaryColour(value.primary_colour || '')
    .secondaryColour(value.secondary_colour || '')
    .primaryShape(value.primary_shape || '')
    .secondaryShape(value.secondary_shape || '')
    .isWorkingDay(value.is_working_day)
    .build();
};

export const mapMomoScheduleWeek: MapFromFn<MomoScheduleWeekDto, MomoScheduleWeek> = (value) =>
  Builder.forModel(MomoScheduleWeek)
    .days((value?.days || []).map((day) => mapMomoScheduleDay(day)))
    .build();

export const mapMomoScheduleMonth: MapFromFn<MomoScheduleMonthDto, MomoScheduleMonth> = (value) =>
  Builder.forModel(MomoScheduleMonth)
    .monthLabel(value.month_label)
    .monthNumber(value.month_number)
    .yearLabel(value.year_label)
    .weeks((value?.weeks || []).map((week) => mapMomoScheduleWeek(week)))
    .observations(mapMomoScheduleObservations(value.observations))
    .build();

export const mapMomoScheduleObservations: MapFromFn<MomoScheduleObservationsDto, MomoScheduleObservations> = (value) =>
  Builder.forModel(MomoScheduleObservations)
    .lastMonthHours(value.last_month_accumulated_hours)
    .theoricalHours(value.theoretical_hours)
    .workedHours(value.worked_hours)
    .currentMonthHours(value.current_month_accumulated_hours)
    .daysOffTaken(value.days_off_taken_this_year)
    .notes(value.notes)
    .lastUpdatedDate(value.last_updated_date || '')
    .build();

export const mapMomoScheduleDayShift: MapFromFn<MomoScheduleDayShiftDto | null, MomoScheduleDayShift | null> = (
  value
) => {
  if (!value) {
    return null;
  }
  return Builder.forModel(MomoScheduleDayShift)
    .start(value.start)
    .end(value.end)
    .shiftType(value.shift_type as ShiftTypes)
    .workingHours(value.working_hours)
    .shiftColor(ShiftTypesColors[value.shift_type as keyof typeof ShiftTypesColors])
    .shiftBgColor(ShiftTypesBackgroundColors[value.shift_type as keyof typeof ShiftTypesBackgroundColors])
    .build();
};

export const mapMomoScheduleDayTaskList: MapFromFn<MomoScheduleDayTaskListDto, MomoScheduleDayTaskList> = (value) =>
  Builder.forModel(MomoScheduleDayTaskList).label(value.label).build();
